/**
 * Operation Service
 * 
 * Handles all operation-related API calls.
 * 
 * @validates Requirements 8.1 - Operation management
 */

import api from './api';
import { Operation, CreateOperationRequest, UpdateOperationRequest, ApiResponse, ExportFormat, ExportColumn, OperationStatus } from '@budgira/shared';

export interface OperationFilters {
  startDate?: string;
  endDate?: string;
  categoryId?: string;
  counterpartyId?: string;
  paymentMethodId?: string;
  status?: OperationStatus[];
  direction?: 'debit' | 'credit';
  search?: string;
}

export interface PaginatedOperations {
  items: Operation[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface OperationWithBalance extends Operation {
  runningBalance: number;
}

export const operationService = {
  /**
   * Get operations for an account with filters and pagination
   */
  async list(
    accountId: string,
    filters?: OperationFilters,
    page = 1,
    pageSize = 25,
    sortBy = 'paymentDate',
    sortOrder: 'asc' | 'desc' = 'desc'
  ): Promise<ApiResponse<PaginatedOperations>> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('pageSize', String(pageSize));
    params.append('sortBy', sortBy);
    params.append('sortOrder', sortOrder);
    params.append('withBalance', 'true'); // Request running balance for each operation
    
    if (filters) {
      // Map frontend filter names to backend expected names
      const filterMapping: Record<string, string> = {
        startDate: 'dateFrom',
        endDate: 'dateTo',
      };
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          const paramName = filterMapping[key] || key;
          params.append(paramName, String(value));
        }
      });
    }
    
    const response = await api.get<ApiResponse<PaginatedOperations>>(
      `/accounts/${accountId}/operations?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get a specific operation
   */
  async getById(id: string): Promise<ApiResponse<OperationWithBalance>> {
    const response = await api.get<ApiResponse<OperationWithBalance>>(`/operations/${id}`);
    return response.data;
  },

  /**
   * Create a new operation
   */
  async create(accountId: string, data: CreateOperationRequest): Promise<ApiResponse<Operation>> {
    const response = await api.post<ApiResponse<Operation>>(`/accounts/${accountId}/operations`, data);
    return response.data;
  },

  /**
   * Update an operation
   */
  async update(id: string, data: UpdateOperationRequest): Promise<ApiResponse<Operation>> {
    const response = await api.put<ApiResponse<Operation>>(`/operations/${id}`, data);
    return response.data;
  },

  /**
   * Delete an operation
   */
  async delete(id: string): Promise<ApiResponse<null>> {
    const response = await api.delete<ApiResponse<null>>(`/operations/${id}`);
    return response.data;
  },

  /**
   * Delete multiple operations in batch
   */
  async deleteBatch(operationIds: string[]): Promise<ApiResponse<{ deletedCount: number }>> {
    const response = await api.delete<ApiResponse<{ deletedCount: number }>>('/operations/batch', {
      data: { operationIds },
    });
    return response.data;
  },

  /**
   * Duplicate an operation
   */
  async duplicate(id: string, overrides?: { paymentDate?: string; amount?: number }): Promise<ApiResponse<Operation>> {
    const response = await api.post<ApiResponse<Operation>>(`/operations/${id}/duplicate`, overrides || {});
    return response.data;
  },

  /**
   * Toggle pointing status (legacy - cycles through statuses)
   */
  async togglePoint(id: string): Promise<ApiResponse<Operation>> {
    const response = await api.post<ApiResponse<Operation>>(`/operations/${id}/point`);
    return response.data;
  },

  /**
   * Cycle operation status forward (unpointed → pointed → reconciled)
   */
  async cycleStatusForward(id: string): Promise<ApiResponse<Operation>> {
    const response = await api.post<ApiResponse<Operation>>(`/operations/${id}/cycle-status`, { direction: 'forward' });
    return response.data;
  },

  /**
   * Cycle operation status backward (reconciled → pointed → unpointed)
   */
  async cycleStatusBackward(id: string): Promise<ApiResponse<Operation>> {
    const response = await api.post<ApiResponse<Operation>>(`/operations/${id}/cycle-status`, { direction: 'backward' });
    return response.data;
  },

  /**
   * Reconcile an operation with a specific accounting date
   */
  async reconcileWithDate(id: string, accountingDate: string): Promise<ApiResponse<Operation>> {
    const response = await api.post<ApiResponse<Operation>>(`/operations/${id}/reconcile`, { accountingDate });
    return response.data;
  },

  /**
   * Set operation status directly
   */
  async setStatus(id: string, status: OperationStatus): Promise<ApiResponse<Operation>> {
    const response = await api.put<ApiResponse<Operation>>(`/operations/${id}`, { status });
    return response.data;
  },

  /**
   * Create a transfer between two accounts
   */
  async createTransfer(data: {
    sourceAccountId: string;
    destinationAccountId: string;
    paymentDate: string;
    accountingDate?: string;
    amount: number;
    debitCategoryId: string;
    creditCategoryId: string;
    reference?: string;
    comment?: string;
    status?: OperationStatus;
  }): Promise<ApiResponse<{ sourceOperation: Operation; destinationOperation: Operation }>> {
    const response = await api.post<ApiResponse<{ sourceOperation: Operation; destinationOperation: Operation }>>('/operations/transfer', data);
    return response.data;
  },

  /**
   * Export operations
   */
  async export(
    accountId: string,
    format: ExportFormat,
    filters?: OperationFilters,
    columns?: ExportColumn[],
    delimiter?: string,
    columnHeaders?: Record<string, string>,
    yesLabel?: string,
    noLabel?: string
  ): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', format);
    
    // Add columns if specified
    if (columns && columns.length > 0) {
      params.append('columns', columns.join(','));
    }
    
    // Add delimiter for CSV
    if (delimiter && format === 'csv') {
      params.append('delimiter', delimiter);
    }
    
    // Add column headers for localization
    if (columnHeaders && Object.keys(columnHeaders).length > 0) {
      params.append('columnHeaders', JSON.stringify(columnHeaders));
    }
    
    // Add yes/no labels for localization
    if (yesLabel) {
      params.append('yesLabel', yesLabel);
    }
    if (noLabel) {
      params.append('noLabel', noLabel);
    }
    
    if (filters) {
      // Map frontend filter names to backend expected names
      const filterMapping: Record<string, string> = {
        startDate: 'dateFrom',
        endDate: 'dateTo',
      };
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          const paramName = filterMapping[key] || key;
          params.append(paramName, String(value));
        }
      });
    }
    
    const response = await api.get(`/accounts/${accountId}/operations/export?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export default operationService;
